import is from '@sindresorhus/is';

import type { CertificateHistoryItem } from '../../../../../shared/source/data-contract/certificate-history';
import type { User } from '../../../../../shared/source/data-contract/user';

export const route = {
    params: {
        certificateId: 'certificateId',
        courseHistoryId: 'courseHistoryId',
        educationHistoryId: 'educationHistoryId',
        projectHistoryId: 'projectHistoryId',
        publicationHistoryId: 'publicationHistoryId',
        userId: 'userId',
        workHistoryId: 'workHistoryId',
    },
    patterns: {
        individualUser: {
            aboutMe: { edit: '/users/:userId/about-me/edit' },
            ambitions: { edit: '/users/:userId/ambitions/edit' },
            certificates: {
                create: '/users/:userId/certificates/create',
                edit: '/users/:userId/certificates/:certificateId/edit',
            },
            contactDetails: {
                edit: '/users/:userId/contact-details/edit',
            },
            courseHistory: {
                create: '/users/:userId/course-history/create',
                edit: '/users/:userId/course-history/:courseHistoryId/edit',
            },
            educationHistory: {
                create: '/users/:userId/education-history/create',
                edit: '/users/:userId/education-history/:educationHistoryId/edit',
            },
            languages: { edit: '/users/:userId/languages/edit' },
            personalInformation: {
                edit: '/users/:userId/personal-information/edit',
            },
            profileDetails: {
                edit: '/users/:userId/profile-details/edit',
            },
            projectHistory: {
                create: '/users/:userId/project-history/create',
                edit: '/users/:userId/project-history/:projectHistoryId/edit',
            },
            publicationHistory: {
                create: '/users/:userId/publication-history/create',
                edit: '/users/:userId/publication-history/:publicationHistoryId/edit',
            },
            root: '/users/:userId',
            skills: { edit: '/users/:userId/skills/edit' },
            workHistory: {
                create: '/users/:userId/work-history/create',
                edit: '/users/:userId/work-history/:workHistoryId/edit',
            },
        },
        users: '/users',
        usersMe: '/users/me',
    },
    searchParams: {
        certificate: {
            id: 'certificateId',
            creating: 'creatingCertificate',
            deleting: 'deletingCertificate',
            editing: 'editingCertificate',
        },
        courseHistory: {
            id: 'courseHistoryId',
            creating: 'creatingCourseHistory',
            deleting: 'deletingCourseHistory',
            editing: 'editingCourseHistory',
        },
        editingAboutMe: 'editingAboutMe',
        editingGeneralInformation: 'editingGeneralInformation',
        educationHistory: {
            id: 'educationHistoryId',
            creating: 'creatingEducationHistory',
            deleting: 'deletingEducationHistory',
            editing: 'editingEducationHistory',
        },
        learningGoals: { addLimitExceeded: 'addLearningGoalsLimitExceeded' },
        openContactDetails: 'openContactDetails',
        projectHistory: {
            id: 'projectHistoryId',
            creating: 'creatingProjectHistory',
            deleting: 'deletingProjectHistory',
            editing: 'editingProjectHistory',
        },
        publicationHistory: {
            id: 'publicationHistoryId',
            creating: 'creatingPublicationHistory',
            deleting: 'deletingPublicationHistory',
            editing: 'editingPublicationHistory',
        },
        skills: {
            id: 'skillId',
            addingNew: 'addingNewSkills',
            addLimitExceeded: 'addSkillLimitExceeded',
            editAttachmentDialog: 'attachSkillDialog',
            searchInputFocused: 'searchSkillInputFocused',
            searchTerm: 'addSkillSearchTerm',
        },
        userSettings: {
            deletingAccount: 'deletingAccount',
            open: 'openUserSettings',
        },
        workHistory: {
            id: 'workHistoryId',
            creating: 'creatingWorkHistory',
            deleting: 'deletingWorkHistory',
            editing: 'editingWorkHistory',
        },
    },
};

export type UserProfileAnchor =
    | 'about-me'
    | 'ambitions'
    | 'certificates'
    | 'course-history'
    | 'education-history'
    | 'general-information'
    | 'languages'
    | 'project-history'
    | 'publication-history'
    | 'skills'
    | 'work-history';

export const userProfileUrl = (userId: string, anchor: UserProfileAnchor | null = null): string =>
    is.null(anchor) === false ? `${route.patterns.users}/${userId}#${anchor}` : `${route.patterns.users}/${userId}`;

export const editUserSkillsUrl = (userId: string): string =>
    route.patterns.individualUser.skills.edit.replace(':userId', userId);
export const editUserAboutMeUrl = (userId: string): string =>
    route.patterns.individualUser.aboutMe.edit.replace(':userId', userId);
export const editUserAmbitionsUrl = (userId: string): string =>
    route.patterns.individualUser.ambitions.edit.replace(':userId', userId);
export const editUserContactUrl = (userId: string): string =>
    route.patterns.individualUser.contactDetails.edit.replace(':userId', userId);
export const editUserPersonalInfoUrl = (userId: string): string =>
    route.patterns.individualUser.personalInformation.edit.replace(':userId', userId);
export const editUserProfileDetailsUrl = (userId: string): string =>
    route.patterns.individualUser.profileDetails.edit.replace(':userId', userId);
export const editUserLanguagesUrl = (userId: string): string =>
    route.patterns.individualUser.languages.edit.replace(':userId', userId);
export const createUserWorkHistoryUrl = (userId: string): string =>
    route.patterns.individualUser.workHistory.create.replace(':userId', userId);
export const editUserWorkHistoryUrl = (userId: string, workHistoryId: string): string =>
    route.patterns.individualUser.workHistory.edit.replace(':userId', userId).replace(':workHistoryId', workHistoryId);
export const createUserEducationHistoryUrl = (userId: string): string =>
    route.patterns.individualUser.educationHistory.create.replace(':userId', userId);
export const editUserEducationHistoryUrl = (userId: string, educationHistoryId: string): string =>
    route.patterns.individualUser.educationHistory.edit
        .replace(':userId', userId)
        .replace(':educationHistoryId', educationHistoryId);
export const createUserCourseHistoryUrl = (userId: string): string =>
    route.patterns.individualUser.courseHistory.create.replace(':userId', userId);
export const editUserCourseHistoryUrl = (userId: string, courseHistoryId: string): string =>
    route.patterns.individualUser.courseHistory.edit
        .replace(':userId', userId)
        .replace(':courseHistoryId', courseHistoryId);
export const createUserProjectHistoryUrl = (userId: string): string =>
    route.patterns.individualUser.projectHistory.create.replace(':userId', userId);
export const editUserProjectHistoryUrl = (userId: string, projectHistoryId: string): string =>
    route.patterns.individualUser.projectHistory.edit
        .replace(':userId', userId)
        .replace(':projectHistoryId', projectHistoryId);
export const createUserPublicationHistoryUrl = (userId: string): string =>
    route.patterns.individualUser.publicationHistory.create.replace(':userId', userId);
export const editUserPublicationHistoryUrl = (userId: string, publicationHistoryId: string): string =>
    route.patterns.individualUser.publicationHistory.edit
        .replace(':userId', userId)
        .replace(':publicationHistoryId', publicationHistoryId);
export const createUserCertificateUrl = (userId: string): string =>
    route.patterns.individualUser.certificates.create.replace(':userId', userId);
export const editUserCertificateUrl = (userId: User['id'], certificateId: CertificateHistoryItem['id']): string =>
    route.patterns.individualUser.certificates.edit.replace(':userId', userId).replace(':certificateId', certificateId);
